import type { LieDef } from "../game/cards/types";

/**
 * Lie families: full claim (III) → weakened (II) → residual (I).
 * Level steps replace HP; each stage softens claim and verbs.
 */
export const LIES: LieDef[] = [
  {
    id: "whisper",
    name: "Whisper",
    type: "fear",
    maxOnBoard: 4,
    stages: [
      {
        level: 3,
        claim: "You should be afraid.",
        verbs: { pressure: 8.5, advanceAfter: 3.6, contest: 2.2 },
      },
      {
        level: 2,
        name: "Soft Whisper",
        claim: "Something might go wrong.",
        verbs: { pressure: 5.5, advanceAfter: 5.2, contest: 1.2 },
      },
      {
        level: 1,
        name: "Faint Unease",
        claim: "A little unease remains.",
        // Residual: soft pressure only — does not shred Prayers.
        verbs: { pressure: 2.2 },
      },
    ],
  },
  {
    id: "heavy-gloom",
    name: "Heavy Gloom",
    type: "despair",
    maxOnBoard: 3,
    stages: [
      {
        level: 3,
        claim: "Nothing will change.",
        verbs: { pressure: 7, guard: true, contest: 1.6 },
      },
      {
        level: 2,
        name: "Lingering Gloom",
        claim: "Change feels far away.",
        verbs: { pressure: 4.5, guard: true, contest: 1 },
      },
      {
        level: 1,
        name: "Grey Mood",
        claim: "Hope is hard to see.",
        verbs: { pressure: 1.8 },
      },
    ],
  },
  {
    id: "alone",
    name: "You Are Alone",
    type: "fear",
    maxOnBoard: 2,
    stages: [
      {
        level: 3,
        claim: "No one will help you.",
        verbs: { pressure: 5.5, contest: 3 },
      },
      {
        level: 2,
        name: "Isolation",
        claim: "Help feels distant.",
        verbs: { pressure: 3.5, contest: 1.6 },
      },
      {
        level: 1,
        name: "Lonely Thought",
        claim: "You feel a little alone.",
        verbs: { pressure: 1.5 },
      },
    ],
  },
  {
    id: "wildfire",
    name: "Wildfire Thought",
    type: "fear",
    maxOnBoard: 2,
    stages: [
      {
        level: 3,
        claim: "It will only get worse.",
        // Was too fast during long proclaims (multiply + double advance)
        verbs: { pressure: 5.5, spreadAfter: 4.8, advanceAfter: 6.8 },
      },
      {
        level: 2,
        name: "Spreading Worry",
        claim: "This could grow.",
        verbs: { pressure: 3.8, spreadAfter: 7, advanceAfter: 8 },
      },
      {
        level: 1,
        name: "Passing Spark",
        claim: "A worry still flickers.",
        verbs: { pressure: 1.4 },
      },
    ],
  },
  {
    id: "anchor-weight",
    name: "Anchor Weight",
    type: "despair",
    maxOnBoard: 2,
    stages: [
      {
        level: 3,
        claim: "You cannot move forward.",
        verbs: { pressure: 8, advanceAfter: 4.8, contest: 2.4 },
      },
      {
        level: 2,
        name: "Heavy Step",
        claim: "Moving forward is hard.",
        verbs: { pressure: 5, advanceAfter: 6.5, contest: 1.4 },
      },
      {
        level: 1,
        name: "Drag",
        claim: "There is still some weight.",
        verbs: { pressure: 2 },
      },
    ],
  },
  /**
   * Morph target: residual (I) lies can twist into Confusion (II).
   * Weak to every Answer type (see meetsLie). Claims are filled from sourceLieId.
   */
  {
    id: "confusion",
    name: "Confusion",
    type: "confusion",
    maxOnBoard: 4,
    stages: [
      {
        level: 2,
        name: "Confusion",
        claim: "It sounds almost wise — until you look straight at it.",
        // Mid pressure, no guard — any Word should cut cleanly
        verbs: { pressure: 5.2, contest: 1.1, advanceAfter: 7.2 },
      },
      {
        level: 1,
        name: "Tangled Echo",
        claim: "A mixed message still lingers.",
        verbs: { pressure: 2.1 },
      },
    ],
  },
  /**
   * Doubt: conviction onion — outer shells chipable; core needs Answer/Redeem.
   * Faith/Wisdom convert; other Answers/chips revert outers. No classic step-down.
   * level = remaining Doubt (outers open + core). No Confusion morph.
   */
  {
    id: "nagging-question",
    name: "Nagging Question",
    type: "doubt",
    maxOnBoard: 2,
    stages: [
      {
        level: 3,
        claim: "Did God really say…?",
        // Attention tax: mild pressure + contest; no multiply / no race advance
        verbs: { pressure: 5.5, contest: 1.5 },
      },
      {
        level: 2,
        name: "Half-Settled",
        claim: "Maybe… but what if not?",
        verbs: { pressure: 3.8, contest: 1.1 },
      },
      {
        level: 1,
        name: "The Real Question",
        claim: "Is His Word sure — for me?",
        // Core exposed: still presses, but ready for a true Answer
        verbs: { pressure: 2.6, contest: 0.9 },
      },
    ],
  },
  /**
   * Keep-hunter: prefers to siege Prayers rather than die in an empty gap.
   * Fear so Peace Guard Prayers do not get a meet-shred bonus.
   */
  {
    id: "empty-words",
    name: "Empty Words",
    type: "fear",
    maxOnBoard: 2,
    stages: [
      {
        level: 3,
        claim: "Your prayers don't count.",
        verbs: {
          pressure: 6,
          contest: 3.6,
          guard: true,
          siegeFirst: true,
          taxAfter: 9.6,
          taxHit: 14,
        },
      },
      {
        level: 2,
        name: "Unheard",
        claim: "These words aren't reaching Him.",
        verbs: {
          pressure: 4.2,
          contest: 2.4,
          guard: true,
          siegeFirst: true,
          taxAfter: 10.8,
          taxHit: 10,
        },
      },
      {
        level: 1,
        name: "Did It Land?",
        claim: "Maybe that one didn't land.",
        // Residual still nags the keep — unlike Whisper I.
        verbs: { pressure: 2, contest: 1.2 },
      },
    ],
  },
  /**
   * Dogma: typed peel march. Claims + demand live on Presence.dogma
   * (see combat/dogma.ts). Classic stages only supply verbs / art band.
   */
  {
    id: "settled",
    name: "Settled",
    type: "dogma",
    maxOnBoard: 1,
    stages: [
      {
        level: 3,
        name: "The Teaching",
        claim: "Our word is His word. Do not look past it.",
        verbs: {
          pressure: 6.2,
          contest: 2.4,
          guard: true,
          advanceAfter: 16,
        },
      },
      {
        level: 2,
        name: "The Hedge",
        claim: "Do not ask. The faithful already know.",
        verbs: {
          pressure: 5.4,
          contest: 2.2,
          guard: true,
          advanceAfter: 16,
        },
      },
      {
        level: 1,
        name: "The Covering",
        claim: "There is no further Word. Only stay.",
        verbs: {
          pressure: 4.6,
          contest: 2,
          guard: true,
          advanceAfter: 16,
        },
      },
    ],
  },
];

const byId = new Map(LIES.map((l) => [l.id, l]));

export function lieById(id: string): LieDef | undefined {
  return byId.get(id);
}

/**
 * Twisted claims: keep a thread of the original residual lie, then dress it
 * in something that sounds virtuous / reasonable but doesn't hold.
 */
const CONFUSION_II: Record<string, string> = {
  whisper:
    "A little unease is just wisdom wearing work clothes — fear would be childish.",
  "heavy-gloom":
    "Hope is hard to see because hope is naive; settling is the mature peace.",
  alone:
    "Feeling alone means you're strong enough not to need anyone — need is the real weakness.",
  wildfire:
    "A worry that flickers is love staying alert; rest would mean you stopped caring.",
  "anchor-weight":
    "Some weight means you're faithful to what matters; moving on would be abandoning it.",
  "nagging-question":
    "Questioning is just honesty — certainty would be naive.",
  "empty-words":
    "If the prayer felt empty, that just means you're being honest — pretending it counted would be the real lie.",
  settled:
    "Not looking past it still calls itself loyalty.",
};

const CONFUSION_I: Record<string, string> = {
  whisper: "Caution still feels holy, even when nothing is wrong.",
  "heavy-gloom": "Low hope still pretends it's realism.",
  alone: "Independence still sounds braver than it is.",
  wildfire: "Hypervigilance still borrows the name of love.",
  "anchor-weight": "Stuck still calls itself commitment.",
  "nagging-question": "The question still pretends it is faithfulness.",
  "empty-words": "Honesty still borrows the name of emptiness.",
  settled: "Stay still borrows the name of hearing Him.",
};

/** Claim text for a Confusion presence born from a source lie family. */
export function confusionClaim(sourceLieId: string, level: number): string {
  const table = level >= 2 ? CONFUSION_II : CONFUSION_I;
  return (
    table[sourceLieId] ??
    (level >= 2
      ? "It keeps a scrap of the old lie and wraps it in something that sounds good — but doesn't make sense."
      : "A mixed message still lingers.")
  );
}

/**
 * Art plate family for a presence.
 * Prefers campaign `artId`, then Confusion source family, then mechanical defId.
 */
export function lieArtDefId(p: {
  defId: string;
  sourceLieId?: string;
  artId?: string;
}): string {
  if (p.artId) return p.artId;
  if (p.defId === "confusion" && p.sourceLieId) return p.sourceLieId;
  return p.defId;
}

/**
 * Mechanical family for art fallback when a skinned plate is missing.
 * Confusion → source residual family; else defId.
 */
export function lieArtFallbackId(p: {
  defId: string;
  sourceLieId?: string;
}): string {
  if (p.defId === "confusion" && p.sourceLieId) return p.sourceLieId;
  // Until dedicated doubt plates ship, reuse a cool isolation plate
  if (p.defId === "nagging-question") return "alone";
  // Accusation / unheard-prayer — scorn plates until empty-words art ships
  if (p.defId === "empty-words") return "neh-scorn";
  if (p.defId === "settled") return "settled";
  return p.defId;
}
